import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import BrandStripe from "./BrandStripe";
import SectionHeader from "./SectionHeader";
import { STYLES } from "@/data/products";
import { TID } from "@/lib/testIds";

const cardAccents = ["var(--ge-yellow)", "var(--ge-red)", "var(--ge-green)", "var(--ge-navy)", "var(--ge-yellow)", "var(--ge-red)", "var(--ge-green)", "var(--ge-navy)"];

export const ProductGallery = ({ hideBrandStripe = false }) => {
  return (
    <section id="range" data-testid={TID.gallery.root} className="relative bg-white py-24 md:py-32">
      <div className="ge-container">
        <SectionHeader
          kicker="The Range"
          title={<>Eight design-led<br /><span style={{ color: "var(--ge-red)" }}>styles</span>.</>}
          subtitle="Every style comes in the five colour-coded strengths. Tap a colour swatch to preview the frame in a different finish."
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {STYLES.map((p, i) => (
            <ProductCard key={p.slug} product={p} accent={cardAccents[i % cardAccents.length]} index={i} />
          ))}
        </div>
      </div>

      {!hideBrandStripe && (
        <div className="absolute bottom-0 left-0 right-0">
          <BrandStripe height={22} />
        </div>
      )}
    </section>
  );
};

export const ProductCard = ({ product, accent, index = 0 }) => {
  const [idx, setIdx] = useState(0);
  const active = product.variants[idx];

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className="group relative bg-white rounded-2xl border border-[color:var(--ge-navy)]/10 p-5 md:p-6 flex flex-col overflow-hidden hover:border-[color:var(--ge-navy)]/40 transition-colors"
      data-testid={TID.gallery.card(product.name)}
    >
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }} />

      <div className="mt-3 flex items-start justify-between text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">
        <span>0{index + 1}</span>
        <span>{active.color}</span>
      </div>

      <Link to={`/product/${product.slug}`} className="mt-2 h-44 md:h-52 flex items-center justify-center relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={active.img}
            src={active.img}
            alt={`${product.name} in ${active.color}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35 }}
            className="max-h-full w-auto product-img"
          />
        </AnimatePresence>
      </Link>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <Link to={`/product/${product.slug}`} className="text-2xl md:text-3xl font-bold tight-cap text-[color:var(--ge-navy)] hover:text-[color:var(--ge-red)] transition-colors">
            {product.name}
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">
            {product.variants.length} colours
          </span>
        </div>
        <div className="mt-2 h-[3px] w-8 rounded-full" style={{ background: accent }} />
        <p className="mt-3 text-sm text-[color:var(--ge-navy)]/75 font-light leading-relaxed line-clamp-2">
          {product.tagline}
        </p>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {product.variants.map((v, i) => (
            <button
              key={v.color}
              onClick={() => setIdx(i)}
              aria-label={`Show ${product.name} in ${v.color}`}
              data-testid={`${TID.gallery.card(product.name)}-swatch-${v.color.toLowerCase().replace(/\s+/g, "-")}`}
              className={`w-5 h-5 rounded-full border-2 transition-transform ${idx === i ? "border-[color:var(--ge-navy)] scale-110" : "border-white"}`}
              style={{ backgroundColor: v.swatch, outline: idx === i ? "1px solid rgba(0,31,91,0.15)" : "none" }}
            />
          ))}
        </div>
        <Link to={`/product/${product.slug}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)] group-hover:text-[color:var(--ge-red)] transition-colors">
          Details <ArrowUpRight size={12} />
        </Link>
      </div>
    </motion.article>
  );
};

export default ProductGallery;
